import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
  Chip,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  Dashboard,
  People,
  Subscriptions,
  PersonAdd,
  Settings,
  VisibilityOff,
  Router as DevicesIcon,
  Business,
} from '@mui/icons-material';
import logo from '../../assets/images/EweAlert.png';

const SIDEBAR_WIDTH = 240;

const settingsItems = [
  { text: 'Configuración', icon: <Settings />, path: '/settings' },
];

const Sidebar = ({ open, onClose, variant = 'permanent' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  // Elementos del menú organizados jerárquicamente
  const menuItems = [
    // 1. Panel Principal
    {
      text: 'Dashboard',
      icon: <Dashboard />,
      path: '/dashboard',
      category: 'principal'
    },

    // 2. Gestión de Contactos (base del sistema)
    {
      text: 'Contactos',
      icon: <People />,
      path: '/contacts',
      category: 'gestion'
    },

    // 3. Distribuidores (gestión de distribuidores)
    {
      text: 'Distribuidores',
      icon: <Business />,
      path: '/distributors',
      category: 'gestion'
    },

    // 4. Dispositivos (gestión de dispositivos IoT)
    {
      text: 'Dispositivos',
      icon: <DevicesIcon />,
      path: '/devices',
      category: 'gestion'
    },

    // 5. Suscripciones (depende de contactos)
    {
      text: 'Suscripciones',
      icon: <Subscriptions />,
      path: '/subscriptions',
      category: 'gestion'
    },

    // 6. Usuarios (administración del sistema)
    {
      text: 'Usuarios',
      icon: <PersonAdd />,
      path: '/users',
      category: 'admin'
    },
  ];

  const handleItemClick = () => {
    // Cerrar sidebar en móvil después de seleccionar
    if (isMobile && onClose) {
      onClose();
    }
  };

  const sidebarContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Brand */}
      <Box
        sx={{
          paddingY: 4,
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center', // centrado horizontal
          gap: 2,
          minHeight: 90,
          backgroundColor: '#fff',
          position: 'relative',
        }}
      >
        <img
          src={logo}
          alt="Logo eWeAlert"
          style={{ height: 48, width: 'auto', borderRadius: 12 }}
        />
        {/* Botón cerrar en móvil */}
        {isMobile && onClose && (
          <IconButton
            onClick={onClose}
            sx={{
              color: '#6b7280',
              padding: 0.5,
              position: 'absolute',
              right: 16,
              top: 16,
            }}
          >
            <Close />
          </IconButton>
        )}
      </Box>

      {/* Menú Principal */}
      <Box sx={{ flex: 1, paddingY: 2 }}>
        <List sx={{ padding: 0 }}>
          {/* Panel Principal */}
          {menuItems
            .filter(item => item.category === 'principal')
            .map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={item.path}
                  onClick={handleItemClick}
                  sx={{
                    marginX: 2,
                    borderRadius: '8px',
                    paddingY: 1.5,
                    paddingX: 2,
                    backgroundColor: location.pathname === item.path ? '#f3f4f6' : 'transparent',
                    '&:hover': {
                      backgroundColor: '#f9fafb',
                    },
                    ...(location.pathname === item.path && {
                      backgroundColor: '#f3f4f6',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                      },
                    }),
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: location.pathname === item.path ? '#111827' : '#6b7280',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: '0.875rem',
                        fontWeight: location.pathname === item.path ? 600 : 500,
                        color: location.pathname === item.path ? '#111827' : '#6b7280',
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}

          {/* Separador */}
          <Box sx={{ marginY: 2, marginX: 3 }}>
            <Typography
              variant="overline"
              sx={{
                fontSize: '0.65rem',
                fontWeight: 600,
                color: '#9ca3af',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              Gestión
            </Typography>
          </Box>

          {/* Gestión (Contactos y Suscripciones) */}
          {menuItems
            .filter(item => item.category === 'gestion')
            .map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={item.path}
                  onClick={handleItemClick}
                  sx={{
                    marginX: 2,
                    borderRadius: '8px',
                    paddingY: 1.5,
                    paddingX: 2,
                    backgroundColor: location.pathname === item.path ? '#f3f4f6' : 'transparent',
                    '&:hover': {
                      backgroundColor: '#f9fafb',
                    },
                    ...(location.pathname === item.path && {
                      backgroundColor: '#f3f4f6',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                      },
                    }),
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: location.pathname === item.path ? '#111827' : '#6b7280',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: '0.875rem',
                        fontWeight: location.pathname === item.path ? 600 : 500,
                        color: location.pathname === item.path ? '#111827' : '#6b7280',
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}

          {/* Separador */}
          <Box sx={{ marginY: 2, marginX: 3 }}>
            <Typography
              variant="overline"
              sx={{
                fontSize: '0.65rem',
                fontWeight: 600,
                color: '#9ca3af',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}
            >
              Administración
            </Typography>
          </Box>

          {/* Administración (Usuarios) */}
          {menuItems
            .filter(item => item.category === 'admin')
            .map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={item.path}
                  onClick={handleItemClick}
                  sx={{
                    marginX: 2,
                    borderRadius: '8px',
                    paddingY: 1.5,
                    paddingX: 2,
                    backgroundColor: location.pathname === item.path ? '#f3f4f6' : 'transparent',
                    '&:hover': {
                      backgroundColor: '#f9fafb',
                    },
                    ...(location.pathname === item.path && {
                      backgroundColor: '#f3f4f6',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                      },
                    }),
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: location.pathname === item.path ? '#111827' : '#6b7280',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: '0.875rem',
                        fontWeight: location.pathname === item.path ? 600 : 500,
                        color: location.pathname === item.path ? '#111827' : '#6b7280',
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
        </List>

        {/* Separador final */}
        <Divider sx={{ marginY: 2, marginX: 2 }} />

        {/* Menú de configuración (siempre al final) */}
        <Box sx={{ paddingY: 2, marginTop: 'auto' }}>
          <List sx={{ padding: 0 }}>
            {settingsItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={item.path}
                  onClick={handleItemClick}
                  sx={{
                    marginX: 2,
                    borderRadius: '8px',
                    paddingY: 1.5,
                    paddingX: 2,
                    backgroundColor: location.pathname === item.path ? '#f3f4f6' : 'transparent',
                    '&:hover': {
                      backgroundColor: '#f9fafb',
                    },
                    ...(location.pathname === item.path && {
                      backgroundColor: '#f3f4f6',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                      },
                    }),
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: location.pathname === item.path ? '#111827' : '#6b7280',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: '0.875rem',
                        fontWeight: location.pathname === item.path ? 600 : 500,
                        color: location.pathname === item.path ? '#111827' : '#6b7280',
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Mejor rendimiento en móvil
      }}
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          borderRight: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          boxShadow: variant === 'temporary' ? '0px 8px 10px -5px rgba(0,0,0,0.2), 0px 16px 24px 2px rgba(0,0,0,0.14), 0px 6px 30px 5px rgba(0,0,0,0.12)' : 'none',
        },
      }}
    >
      {sidebarContent}
    </Drawer>
  );
};

export { SIDEBAR_WIDTH };
export default Sidebar; 